import React from 'react';

import {ProcedureType, ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getProcedureUIString, {ProceduresStringKeys} from 'procedures/ProceduresStringKeys';
import {RenderTaskStatus} from 'studies/typed/shared/ProcedureRunTableUtil';
import {getSharedSmartChipProps, SharedSmartChipProps} from 'ui-library/chips/ChipUtil';

import {BulkChipItemPrefetchQuery} from './__generated/BulkChipItemPrefetchQuery';
import ChipPopoverContent from './ChipPopoverContent';
import {NO_ACCESS_CHIP_TEXT} from './NoAccessChip';
import SmartChip from './SmartChip';
import useSmartChipQuery, {SmartChipType} from './useSmartChipQuery';

type ProcedureMethodExecutionInstance = ExtractByTypename<
  BulkChipItemPrefetchQuery['procedureMethodExecutionInstances'][number],
  'ProcedureMethodExecutionInstanceNode'
>;

interface ProcedureMethodExecutionInstanceChipFields {
  procedureRunName: string;
  procedureRunDisplayId: string | null;
  planMethodDefinitionVersionName: string;
  procedureRunConditionName: string;
  replicateNumber: number;
  status: ProcessTaskStatusType | null;
}

function getProcedureMethodExecutionInstanceChipFields(
  procedureMethodExecutionInstance: ProcedureMethodExecutionInstance
): ProcedureMethodExecutionInstanceChipFields {
  const procedureRunName = procedureMethodExecutionInstance.procedureRun.name;
  const procedureRunDisplayId = procedureMethodExecutionInstance.procedureRun.displayId;
  const planMethodDefinitionVersionName = procedureMethodExecutionInstance.planMethodDefinitionVersion.name;
  const {replicateNumber, procedureRunCondition} =
    procedureMethodExecutionInstance.procedureRunConditionReplicate;
  const procedureRunConditionName = procedureRunCondition.name;
  const methodTask = procedureMethodExecutionInstance.methodTask;
  const status = methodTask && !isInaccessible(methodTask) ? methodTask.status.statusType : null;

  return {
    procedureRunName,
    procedureRunDisplayId,
    planMethodDefinitionVersionName,
    replicateNumber,
    procedureRunConditionName,
    status,
  };
}

export function ProcedureMethodExecutionInstanceChipPopoverContent({
  pmeiChipFields,
  isInaccessible,
  procedureType,
}: {
  pmeiChipFields: ProcedureMethodExecutionInstanceChipFields;
  isInaccessible: boolean | undefined;
  procedureType: ProcedureType;
}): JSX.Element | null {
  if (isInaccessible) {
    return <>{NO_ACCESS_CHIP_TEXT}</>;
  }
  const {
    procedureRunName,
    planMethodDefinitionVersionName,
    procedureRunConditionName,
    replicateNumber,
    procedureRunDisplayId,
    status,
  } = pmeiChipFields;
  const fields = [
    {
      label: `${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType)} run`,
      value: procedureRunName,
    },
    {label: 'Run ID', value: procedureRunDisplayId},
    {label: 'Unit operation', value: planMethodDefinitionVersionName},
    {label: 'Condition', value: procedureRunConditionName},
    {label: 'Replicate', value: `${replicateNumber}`},
    {label: 'Status', value: <RenderTaskStatus statusType={status} />},
  ];
  return <ChipPopoverContent info={{fields}} />;
}

export default function ProcedureMethodExecutionInstanceChip(
  props: SharedSmartChipProps<SmartChipType.PROCEDURE_METHOD_EXECUTION_INSTANCE>
): JSX.Element {
  const sharedSmartChipProps = getSharedSmartChipProps(props);
  const {id, onClick} = sharedSmartChipProps;

  const {
    item: procedureMethodExecutionInstance,
    handleClickLink,
    isInaccessible,
  } = useSmartChipQuery<SmartChipType.PROCEDURE_METHOD_EXECUTION_INSTANCE>({
    id,
    onClick,
  });

  let procedureMethodExecutionInstanceItem;
  let popoverContent;
  if (procedureMethodExecutionInstance) {
    const pmeiChipFields = getProcedureMethodExecutionInstanceChipFields(procedureMethodExecutionInstance);
    procedureMethodExecutionInstanceItem = {
      ...procedureMethodExecutionInstance,
      name: `${pmeiChipFields.procedureRunName} ${pmeiChipFields.planMethodDefinitionVersionName} ${pmeiChipFields.procedureRunConditionName} - ${pmeiChipFields.replicateNumber}`,
    };
    popoverContent = (
      <ProcedureMethodExecutionInstanceChipPopoverContent
        pmeiChipFields={pmeiChipFields}
        isInaccessible={isInaccessible}
        procedureType={procedureMethodExecutionInstance.procedureRun.procedureType}
      />
    );
  }

  return (
    <SmartChip
      {...sharedSmartChipProps}
      icon={null}
      isInaccessible={isInaccessible}
      item={procedureMethodExecutionInstanceItem}
      onClick={handleClickLink}
      popoverContentOverride={popoverContent}
      url={null}
      isReadonly={true}
    />
  );
}
